import {
  ConversationDetail,
  ConversationMessage,
  ConversationSummary,
} from './types'

const API_BASE_URL = 'https://api.jgp.com.br/compass_llm/api/v1'
//const API_BASE_URL = 'http://localhost:8001/compass_llm/api/v1'

// Get authentication token
const getAuthToken = async (): Promise<string> => {
  const tokenResponse = await fetch('/api/auth/token', {
    credentials: 'include',
  })

  if (!tokenResponse.ok) {
    throw new Error('Failed to get authentication token')
  }

  const { token } = await tokenResponse.json()
  return token
}

// Create headers with auth token
const createHeaders = async (): Promise<HeadersInit> => {
  const token = await getAuthToken()
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  }
}

export const conversationApi = {
  // List conversations
  async listConversations(
    limit = 50,
    offset = 0
  ): Promise<ConversationSummary[]> {
    const headers = await createHeaders()
    const response = await fetch(
      `${API_BASE_URL}/conversations/?limit=${limit}&skip=${offset}`,
      { headers }
    )

    if (!response.ok) {
      throw new Error('Failed to fetch conversations')
    }

    const data = await response.json()
    return data.data || []
  },

  // Get conversation by ID
  async getConversation(id: string): Promise<ConversationDetail> {
    const headers = await createHeaders()
    const response = await fetch(`${API_BASE_URL}/conversations/${id}`, {
      headers,
    })

    if (!response.ok) {
      throw new Error('Failed to fetch conversation')
    }

    const data = await response.json()
    return data.data
  },

  // Create new conversation
  async createConversation(
    title: string,
    messages: ConversationMessage[] = []
  ): Promise<ConversationDetail> {
    const headers = await createHeaders()
    const response = await fetch(`${API_BASE_URL}/conversations/`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ title, messages }),
    })

    if (!response.ok) {
      throw new Error('Failed to create conversation')
    }

    const data = await response.json()
    return data.data
  },

  // Update conversation
  async updateConversation(
    id: string,
    updates: { title?: string; messages?: ConversationMessage[] }
  ): Promise<ConversationDetail> {
    const headers = await createHeaders()
    const response = await fetch(`${API_BASE_URL}/conversations/${id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(updates),
    })

    if (!response.ok) {
      throw new Error('Failed to update conversation')
    }

    const data = await response.json()
    return data.data
  },

  // Delete conversation
  async deleteConversation(id: string): Promise<void> {
    const headers = await createHeaders()
    const response = await fetch(`${API_BASE_URL}/conversations/${id}`, {
      method: 'DELETE',
      headers,
    })

    if (!response.ok) {
      throw new Error('Failed to delete conversation')
    }
  },

  // Delete all conversations
  async deleteAllConversations(): Promise<void> {
    const headers = await createHeaders()
    const response = await fetch(`${API_BASE_URL}/conversations/`, {
      method: 'DELETE',
      headers,
    })

    if (!response.ok) {
      throw new Error('Failed to delete all conversations')
    }
  },

  // Add messages to conversation
  async addMessages(
    id: string,
    messages: ConversationMessage[]
  ): Promise<ConversationDetail> {
    const headers = await createHeaders()
    const response = await fetch(
      `${API_BASE_URL}/conversations/${id}/messages`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({ messages }),
      }
    )

    if (!response.ok) {
      throw new Error('Failed to add messages to conversation')
    }

    const data = await response.json()
    return data.data
  },
}
